/*
 * Share Builder Page Panel Template
 *
 * Includes:
 * - Share Builder Page
 */
import type {
  PanelArea,
  PanelsBreakpointTemplate,
  PanelsTemplate,
  ShareBuilderPanelArea,
} from '../types';
import {
  DETAILS_DEFAULT_WIDTH,
  DETAILS_MIN_WIDTH,
  LARGE_NAV_DEFAULT_WIDTH,
  MEDIUM_NAV_MAX_WIDTH,
  MEDIUM_NAV_MIN_WIDTH,
  PROJECT_NAV_MIN_WIDTH,
} from './constants';
import TEXT from './generated/TEXT/shareBuilder';

TEXT.DEFINE({
  directoryNav: {
    accessibility: 'Toggle the Project Navigation',
    hide: 'Hide Project Navigation',
    show: 'Show Project Navigation',
  },
  directoryContent: {
    accessibility: 'Toggle the Share Preview',
    hide: 'Hide the Share Preview',
    show: 'Show the Share Preview',
  },
  details: {
    accessibility: 'Toggle the asset Info',
    hide: 'Hide Asset Info',
    show: 'Show Asset Info',
  },
  /** AI panel toggle labels */
  ai: {
    /** Aria label for toggling AI panel */
    accessibility: 'Toggle the AI Panel',
    /** Label when hiding AI panel */
    hide: 'Hide AI Panel',
    /** Label when showing AI panel */
    show: 'Show AI Panel',
  },
});

/**
 * Base template (Mobile +)
 */
export function baseTemplate(
  visible: Array<PanelArea>
): PanelsBreakpointTemplate {
  return {
    areas: [
      ['directoryNav', 'directoryContent', 'viewer', 'ai'],
      ['directoryNav', 'details', 'details', 'ai'],
    ],
    resizers: {
      directoryNav: 'right',
      viewer: 'bottom',
      directoryContent: 'bottom',
    },
    columns: [
      /* Project Nav uses the PrimarySheetNav tunnel on smaller screens so it's always hidden. */
      {
        key: 'directoryNav',
        defaultSize: MEDIUM_NAV_MIN_WIDTH,
        isToggleable: false,
        showing: false,
      },

      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        panelToggle: {
          label: TEXT.directoryContent,
          variant: 'center',
          disabled: true,
        },
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultSize: '0fr',
        isToggleable: false,
        showing: false,
      },

      /* AI Panel (hidden on mobile) */
      {
        key: 'ai',
        defaultMin: 316,
        defaultSize: 400,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },

      /* Settings Panel */
      {
        key: 'details',
        defaultMin: DETAILS_DEFAULT_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        isToggleable: true,
        panelToggle: {
          label: TEXT.details,
          variant: 'right',
        },
        showing: visible.includes('details'),
      },
    ],
  };
}

/**
 * Medium template
 */
export const mediumTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [
      ['directoryNav', 'directoryContent', 'viewer', 'ai'],
      ['directoryNav', 'details', 'viewer', 'ai'],
    ],
    resizers: {
      directoryNav: 'right',
      viewer: 'right',
      directoryContent: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: PROJECT_NAV_MIN_WIDTH,
        defaultSize: PROJECT_NAV_MIN_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        panelToggle: {
          label: TEXT.directoryNav,
          variant: 'left',
        },
        showing: visible.includes('directoryNav'),
      },

      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        panelToggle: {
          label: TEXT.directoryContent,
          variant: 'center',
          disabled: true,
        },
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultSize: '0fr',
        isToggleable: false,
        showing: false,
      },

      /* AI Panel */
      {
        key: 'ai',
        defaultMin: 316,
        defaultSize: 400,
        isToggleable: true,
        panelToggle: {
          variant: 'right',
          label: TEXT.ai,
        },
        showing: visible.includes('ai'),
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },

      /* Settings Panel */
      {
        key: 'details',
        defaultMin: DETAILS_MIN_WIDTH,
        defaultSize: DETAILS_MIN_WIDTH,
        defaultMax: 400,
        isToggleable: true,
        panelToggle: {
          label: TEXT.details,
          variant: 'right',
        },
        showing: visible.includes('details'),
      },
    ],
  };
};

/**
 * Large template
 */
export const largeTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryNav: 'right',
      viewer: 'right',
      directoryContent: 'right',
      details: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: PROJECT_NAV_MIN_WIDTH,
        defaultSize: LARGE_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        panelToggle: {
          label: TEXT.directoryNav,
          variant: 'left',
        },
        showing: visible.includes('directoryNav'),
      },

      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 208,
        defaultSize: '1fr',
        panelToggle: {
          label: TEXT.directoryContent,
          variant: 'center',
          disabled: true,
        },
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultSize: '0fr',
        isToggleable: false,
        showing: false,
      },

      /* Settings Panel */
      {
        key: 'details',
        defaultMin: DETAILS_MIN_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        defaultMax: 600,
        isToggleable: true,
        panelToggle: {
          label: TEXT.details,
          variant: 'right',
        },
        showing: visible.includes('details'),
      },

      /* AI Panel */
      {
        key: 'ai',
        defaultMin: 316,
        defaultSize: 400,
        isToggleable: true,
        panelToggle: {
          variant: 'right',
          label: TEXT.ai,
        },
        showing: visible.includes('ai'),
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

export function getPanelState(visible: Array<PanelArea>): PanelsTemplate {
  return {
    base: baseTemplate(visible),
    tablet: mediumTemplate(visible),
    desktop: largeTemplate(visible),
    visible,
  };
}

export const getDefaultVisiblePanels = (): Array<ShareBuilderPanelArea> => {
  return ['directoryNav', 'directoryContent', 'details'];
};

// Because this page encapsulates three "pages", each has a different layout default
export const getDefaultPanelState = (): PanelsTemplate => {
  return getPanelState(getDefaultVisiblePanels());
};
